import {
  BOARD_MIN_SCALE_FACTOR,
  BOARD_PORTRAIT_RATIO,
  SMALL_CARD_HEIGHT,
  SMALL_CARD_WIDTH
} from 'constants/gameConstants';
import { SeatPosition, SeatRingLayout, tableCenterY } from 'hooks/useEllipticalPositions';

import { seatSafeLeftPx } from 'components/Seat/modes/seatEdgeInsets';

import { FELT_SCALE_QUANTUM, seatFootprintAtDesignSize } from './feltScale';

/**
 * How large the COMMUNITY BOARD may be painted before a flank seat covers it.
 *
 * This is the board's half of the rule feltScale states for the seats: cards
 * shrink, they never get covered. feltScale bounds seat against seat and seat
 * against the portrait bet columns. Nothing bounded the board against anything,
 * because until EN-10740 the board did not need it — in portrait it was a bare
 * `scale(0.75)` on a box that was itself sized by the player's Table View Size,
 * so it shrank whenever the player asked for smaller.
 *
 * EN-10740 replaced that with `max(BOARD_MIN_SCALE_FACTOR, --seat-scale)` and a
 * fixed-size card box. The floor was there for a real reason (see
 * BOARD_MIN_SCALE_FACTOR: making the board track the container faithfully
 * dropped a grid cell's community cards from 39px to ~29px). But on a PHONE
 * `--seat-scale` is below 1.15 at every Table View Size, so `max()` always
 * picked the floor and the board became a CONSTANT 44.8px card, 0.8625 painted
 * scale:
 *
 *   - it stopped responding to Table View Size at all on small screens, so a
 *     player who could see the flop card was covered had no way to fix it;
 *   - it was painted LARGER than the build it replaced at every setting below
 *     ~Default (0.8625 against 0.75 x 0.8 = 0.60 at Smallest), so the change
 *     made the occlusion it was meant to fix WORSE on phones.
 *
 * Measured on a 390x660 portrait container, 9-ring, before this module existed:
 * the left flank seat's plate crossed the board's left edge by 2.6px at the
 * DEFAULT setting and 18.2px at Largest — which is the reported screenshot, the
 * first flop card behind a seat plate.
 *
 * A single unconditional floor cannot serve both containers, because they are
 * not the same problem: a grid cell is ~470-480px wide with seats at ~0.66-1.08
 * scale and has room to spare, a 390px phone does not. So the floor STAYS (the
 * grid keeps its legible board) and is BOUNDED by the room actually available.
 */

/** Gap between two board cards, in design px. Portrait's felt is narrower. */
export const boardCardGapPx = (isPortrait: boolean): number => (isPortrait ? 4 : 6);

/**
 * Half the board's width at scale 1, in design px — five cards and four gaps.
 *
 * Shared with HoldEmCards rather than restated there: this value decides both
 * where the board is allowed to reach (here) and the negative margin that
 * reclaims its layout slack (there), and those two disagreeing is how a board
 * ends up a different size from its own footprint.
 */
export const boardHalfWidthAtDesignSize = (isPortrait: boolean): number =>
  (5 * SMALL_CARD_WIDTH + 4 * boardCardGapPx(isPortrait)) / 2;

/** Half the board's height at scale 1, in design px. */
export const boardHalfHeightAtDesignSize = (): number => SMALL_CARD_HEIGHT / 2;

/**
 * Air between the board's outer card edge and the nearest seat, in painted px.
 *
 * NOT zero. Zero means "exactly touching", and a seat plate and a card that
 * touch read as overlapping once both drop shadows are painted — the reported
 * screenshots are a card the player called covered, not a card provably one
 * pixel under a plate. Eight px is the same allowance `SEAT_EDGE_BUFFER_PX`
 * buys a seat against the viewport edge, for the same reason.
 *
 * Painted px, not design px: the gap has to stay visible on a shrunken felt,
 * where scaling it would shrink it away exactly when it is needed most.
 */
export const BOARD_SEAT_CLEARANCE_PX = 8;

/**
 * The board never shrinks below this painted scale — 31px cards.
 *
 * A clearance bound with no floor would happily resolve to a board too small to
 * read, which is not a fix, it is a different bug (and the one
 * BOARD_MIN_SCALE_FACTOR exists to prevent). Below this size legibility wins
 * over the last few px of clearance.
 *
 * Not reached by any supported container: the tightest case modelled — a 390x660
 * phone, 9-ring, Largest — resolves 0.64, and the seat ring's own scale clamp
 * (feltScale) is what keeps it there. If this floor ever becomes the binding
 * term, the ring clamp has stopped doing its job and that is the bug to fix.
 */
export const BOARD_SCALE_MIN = 0.6;

/**
 * The largest board scale at which no seat's painted footprint reaches the
 * board.
 *
 * WHAT IS MODELLED, and why each piece is what it is:
 *
 *  - THE SEAT'S X IS THE CLAMPED ONE (`seatSafeLeftPx`), not the ring's raw
 *    percentage. On a 390px phone the viewport clamp pushes the left flank seat
 *    from 46.8px to 58.4px at Largest, 11.6px nearer the board. Modelling the
 *    raw position is exactly the mistake that let EN-10740 ship believing the
 *    board had ~35px of clearance on the narrowest cell.
 *  - THE SEAT'S REACH IS `seatFootprintAtDesignSize`, the same footprint
 *    feltScale bounds seats against each other with — reused rather than
 *    restated. It is asymmetric on purpose (hole cards fan LEFT, EN-10520), so a
 *    seat LEFT of centre reaches the board with its `right` extent and a seat
 *    RIGHT of centre with its wider `left` one.
 *  - ONLY SEATS LEVEL WITH THE BOARD. A seat high on the ring is nowhere near
 *    the board vertically and charging it would drive the board to its floor for
 *    nothing.
 *  - EVERY SUPPORTED RING, not this table's live seat count — the same reason
 *    feltScale measures across SUPPORTED_SEAT_COUNTS: the board must not resize
 *    when the ninth player sits down.
 *
 * NOT MODELLED, because nothing is left to model: perspective. The board is
 * drawn flat, outside the felt's 3D tilt (EN-11031), so its painted size is
 * exactly the scale computed here.
 *
 * Returns Infinity when nothing can constrain the board, so callers can
 * `Math.min` it unconditionally.
 */
export const maxBoardScaleForSeats = ({
  rings,
  containerWidth,
  containerHeight,
  seatScale,
  isPortrait,
  ringLayout,
  boardScaleWanted
}: {
  /** Seat rings for every supported seat count, in container percentages. */
  rings: SeatPosition[][];
  containerWidth: number;
  containerHeight: number;
  /** The felt's resolved `--seat-scale`. */
  seatScale: number;
  /** Which orientation the SEATS and the board paint in. */
  isPortrait: boolean;
  ringLayout: SeatRingLayout;
  /**
   * The scale the board would paint at unbounded. Used ONLY to decide which
   * seats are vertically level with the board — the answer feeds back into the
   * board's own height, so the unbounded (larger) value is used deliberately:
   * it can only ever include MORE seats, never fewer.
   */
  boardScaleWanted: number;
}): number => {
  if (containerWidth <= 0 || containerHeight <= 0) return Infinity;

  const halfWidth = boardHalfWidthAtDesignSize(isPortrait);
  if (halfWidth <= 0) return Infinity;

  const footprint = seatFootprintAtDesignSize(isPortrait);
  const centreX = containerWidth / 2;
  // The board is centred on the same midline as the seat ring — portrait's
  // content column is at 46% and landscape's at 50%, which is `tableCenterY`
  // for each. (`useEllipticalPositions`' own `boardCentreY` still says a flat
  // 50%; that is a separate, pre-existing disagreement about bet-chip banding
  // and is NOT this module's to resolve — see the ticket notes.)
  const centreY = (tableCenterY(isPortrait, ringLayout) / 100) * containerHeight;
  const boardHalfHeight = boardHalfHeightAtDesignSize() * boardScaleWanted;

  let limit = Infinity;
  for (const seats of rings) {
    for (const seat of seats) {
      const seatY = (seat.topPct / 100) * containerHeight;
      const reachesUp = footprint.above * seatScale;
      const reachesDown = footprint.below * seatScale;
      const clearsAbove = seatY + reachesDown <= centreY - boardHalfHeight;
      const clearsBelow = seatY - reachesUp >= centreY + boardHalfHeight;
      if (clearsAbove || clearsBelow) continue;

      const seatX = seatSafeLeftPx(seat.leftPct, isPortrait, containerWidth, seatScale);
      // Which of the seat's two extents faces the board.
      const inboardReach = (seatX <= centreX ? footprint.right : footprint.left) * seatScale;
      const inboardEdge = seatX + Math.sign(centreX - seatX) * inboardReach;
      const room = Math.abs(centreX - inboardEdge) - BOARD_SEAT_CLEARANCE_PX;
      limit = Math.min(limit, room / halfWidth);
    }
  }
  return limit;
};

/**
 * The board's painted scale for this table.
 *
 * Three terms, in this order:
 *   1. `max(BOARD_MIN_SCALE_FACTOR, seatScale)` — the legibility floor that
 *      keeps a grid cell's board readable, then growth with the felt above it.
 *   2. the portrait ratio — portrait felt is narrow, so its board is smaller.
 *   3. bounded by the room the seats actually leave, and floored at
 *      BOARD_SCALE_MIN.
 *
 * Term 3 is what makes term 1 safe. It is also what gives the player their
 * setting back on a phone: because the flank seats move inboard and grow as
 * Table View Size rises, the room left for the board FALLS with it, so the
 * board now changes size across the slider's travel instead of sitting at a
 * constant 44.8px card. That direction is not a choice, it is the geometry —
 * on a 390px-wide felt, bigger seats mean less middle.
 *
 * Quantised like the felt scale, for the same reason: this value reaches the
 * DOM, and sub-pixel jitter in the measured container would otherwise produce a
 * stream of style writes that each change layout.
 */
export const resolveBoardScale = ({
  rings,
  containerWidth,
  containerHeight,
  seatScale,
  isPortrait,
  ringLayout
}: {
  rings: SeatPosition[][];
  containerWidth: number;
  containerHeight: number;
  seatScale: number;
  isPortrait: boolean;
  ringLayout: SeatRingLayout;
}): number => {
  const wanted =
    Math.max(BOARD_MIN_SCALE_FACTOR, seatScale) * (isPortrait ? BOARD_PORTRAIT_RATIO : 1);
  const limit = maxBoardScaleForSeats({
    rings,
    containerWidth,
    containerHeight,
    seatScale,
    isPortrait,
    ringLayout,
    boardScaleWanted: wanted
  });
  const scale = Math.max(BOARD_SCALE_MIN, Math.min(wanted, limit));
  // FLOOR, not round — rounding can land half a quantum ABOVE the clearance
  // limit, which is the one place being over it is guaranteed to show.
  return Math.floor(scale / FELT_SCALE_QUANTUM) * FELT_SCALE_QUANTUM;
};
